/**
 * The 3D view control of the Map Controls panel: a toggle that tilts the map
 * and raises the grid cells (the layers extrude only while it is on) and,
 * only while it is on, the attribute the cells are raised by and a multiplier
 * on their height. The toggle also follows the map: tilting it by hand
 * switches the view on, and the compass reset switches it off (Map.tsx,
 * NavigationButtons.tsx).
 */
import React from 'react';
import { Box, MenuItem, Select, Slider, ToggleButton, Tooltip, Typography } from '@mui/material';
import ViewInArOutlined from '@mui/icons-material/ViewInArOutlined';
import { FlyToInterpolator } from '@deck.gl/core';
import { useAtom, useSetAtom } from 'jotai';
import {
  selectedElevationKeyAtom,
  selectedElevationScaleAtom,
  viewStateAtom,
  visualModeAtom,
} from '../atoms';
import { attributeOptionsFor } from '../attributeOptions';
import { PanelField, panelSelectSx, panelSliderSx, panelToggleSx } from './panelFields';
import { TEXT_DIM } from './palette';

/** Camera pitch, in degrees, the toggle tilts the map to. */
const THREE_D_PITCH = 60;
const MAX_PITCH = 85;
/** Range of the height multiplier: 0 flattens every cell, 1 draws raw values. */
const HEIGHT_SCALE_MIN = 0;
const HEIGHT_SCALE_MAX = 100;
/** The settings the toggle reveals, so it can say what it controls. */
const SETTINGS_ID = '3d-view-settings';

const THREE_D_TOOLTIP =
  'Tilt the map and raise each grid cell to the value of a height attribute you choose.';
const THREE_D_HINT =
  'Drag the map with the right mouse button (or hold Ctrl while dragging) to rotate and tilt ' +
  'it; the compass button resets the view.';
const HEIGHT_ATTRIBUTE_HINT = 'The attribute each grid cell is raised by in the 3D view.';
const HEIGHT_SCALE_HINT = 'Multiplies every cell height, to exaggerate or flatten the 3D view.';
const NO_ATTRIBUTES_HINT =
  'The layer on the map has no attributes to raise cells by, so the 3D view only tilts the map.';

type Props = {
  /** The section whose grid is on the map, or null when none has attributes. */
  sectionId: string | null;
};

export const ThreeDViewControls: React.FC<Props> = ({ sectionId }) => {
  const [visualMode, setVisualMode] = useAtom(visualModeAtom);
  const setViewState = useSetAtom(viewStateAtom);
  const [elevationKey, setElevationKey] = useAtom(selectedElevationKeyAtom);
  const [elevationScale, setElevationScale] = useAtom(selectedElevationScaleAtom);
  const options = sectionId == null ? null : attributeOptionsFor(sectionId);
  const threeD = visualMode === '3d';

  const toggle = React.useCallback(() => {
    setViewState(vs => ({
      ...vs,
      pitch: threeD ? 0 : THREE_D_PITCH,
      maxPitch: MAX_PITCH,
      transitionInterpolator: new FlyToInterpolator({ speed: 3.0 }),
      transitionDuration: 1000,
    }));
    setVisualMode(threeD ? '2d' : '3d');
  }, [setViewState, threeD, setVisualMode]);

  // The height attribute is shared across sections, so a key this section
  // does not offer shows as no selection rather than as an out-of-range value.
  const heightValue =
    options != null && options.elevation.some(option => option.key === elevationKey)
      ? elevationKey
      : '';

  return (
    <Box>
      <Tooltip title={THREE_D_TOOLTIP} placement="right" describeChild>
        <ToggleButton
          value="3d"
          selected={threeD}
          onChange={toggle}
          aria-label="Toggle 3D view"
          aria-expanded={threeD}
          aria-controls={threeD ? SETTINGS_ID : undefined}
          sx={panelToggleSx}
        >
          <ViewInArOutlined fontSize="small" />
          3D view
        </ToggleButton>
      </Tooltip>

      {threeD && (
        <Box id={SETTINGS_ID} sx={{ mt: 0.75 }}>
          <Typography variant="caption" sx={{ display: 'block', color: TEXT_DIM, lineHeight: 1.3 }}>
            {THREE_D_HINT}
          </Typography>
          {options == null ? (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: TEXT_DIM }}>
              {NO_ATTRIBUTES_HINT}
            </Typography>
          ) : (
            <Box sx={{ mt: 1 }}>
              <PanelField
                id="height-attribute-label"
                label="Height attribute"
                hint={HEIGHT_ATTRIBUTE_HINT}
              >
                <Select
                  fullWidth
                  size="small"
                  labelId="height-attribute-label"
                  value={heightValue}
                  onChange={event => setElevationKey(event.target.value)}
                  sx={panelSelectSx}
                >
                  {options.elevation.map(option => (
                    <MenuItem key={option.key} dense value={option.key}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </PanelField>
              <PanelField id="height-scale-label" label="Height scale" hint={HEIGHT_SCALE_HINT}>
                <Slider
                  size="small"
                  value={elevationScale}
                  min={HEIGHT_SCALE_MIN}
                  max={HEIGHT_SCALE_MAX}
                  valueLabelDisplay="auto"
                  aria-labelledby="height-scale-label"
                  sx={panelSliderSx}
                  onChange={(_event, value) => !Array.isArray(value) && setElevationScale(value)}
                />
              </PanelField>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
